import { Component, Input } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { LoadingController, ModalController, NavParams, ToastController } from '@ionic/angular';
import { isNil } from 'lodash';
import { BaseClass } from 'src/app/shared/classes/base';
import { ProcessConfig, QuestionConfig, QuestionTypeConfig, QuestionTypeEnum } from 'src/app/shared/interfaces/process-config.interface';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { GoalsService } from 'src/app/goals/goals.service';
import { Goal } from 'src/app/goals/_interfaces/goal.interface';
import { ProcessTypeEnum } from '../../interfaces/process.interface';

@Component({
    selector: 'app-new-goals-input',
    templateUrl: './new-goals-input.html',
    styleUrls: ['./new-goals-input.scss'],
})
export class NewGoalsInput extends BaseClass {

    @Input() consent: boolean;
    @Input() processType: ProcessTypeEnum;

    public processConfig: ProcessConfig;
    public formGroup: UntypedFormGroup;
    public language: string;

    private _participantId: number;
    private _goalId: number;

    constructor(
            protected _toastController: ToastController,
            protected _translationService: TranslationService,
            private _modalController: ModalController,
            private _loadingController: LoadingController,
            private _goalsService: GoalsService,
            private _navParams: NavParams,
            private _activatedRoute: ActivatedRoute,
            private _router: Router,
            private _location: Location
    ) {
        super(_toastController, _translationService);
        
        // Try to get data from NavParams first (modal context)
        this._participantId = this._navParams.get('cycleParticipantId');
        this.processConfig = this._navParams.get('processConfig');
        const editingGoal = this._navParams.get('goal');
        this._goalId = editingGoal ? editingGoal.id : null;
        
        // If no data from NavParams, try to get from route data (routing context)
        if (!this.processConfig) {
            this._activatedRoute.data.subscribe(data => {
                if (data.processConfig) {
                    this.processConfig = data.processConfig;
                }
            });
            
            this._activatedRoute.queryParams.subscribe(params => {
                if (params.cycleParticipantId) {
                    this._participantId = params.cycleParticipantId;
                }
                if (params.goalId) {
                    this._goalId = params.goalId;
                }
            });
        }
        
        // Initialize processConfig with minimal default values if still undefined
        if (!this.processConfig) {
            this.processConfig = {
                title: 'Goals Configuration',
                questions: []
            } as any;
        }
        
        // Add defensive check for processConfig
        if (this.processConfig && this.processConfig.questions) {
            this.formGroup = this._createGoalFormGroup(this.processConfig.questions, editingGoal);
        } else {
            // Initialize with empty form if processConfig is not available
            this.formGroup = this._createGoalFormGroup([], editingGoal);
        }
        
        this.language = this._translationService.getSelectedLanguage();
    }

    public dismiss(reload: boolean = false, data: any = null) {
        // Check if we're in a modal context
        this._modalController.getTop().then(modal => {
            if (modal) {
                if (this.consent)
                    this._modalController.dismiss(data);
                else
                    this._modalController.dismiss(reload);
            } else {
                // If not in modal context, navigate back using Location service
                this._location.back();
            }
        }).catch(error => {
            console.log('Not in modal context, navigating back');
            // Alternative close behavior for non-modal usage
            this._location.back();
        });
    }

    public save(formGroup: UntypedFormGroup): void {

        // Add defensive check for processConfig
        if (!this.processConfig || !this.processConfig.questions) {
            console.error('ProcessConfig is not available');
            return;
        }

        if (!this.processConfig.title) {
            let titleIndex = (this.processConfig.questions.findIndex((question) => question.typeConfig.type === QuestionTypeEnum.Title) + 1);
            let titleValue = formGroup.get('field' + titleIndex) ? formGroup.get('field' + titleIndex).value : '';
            formGroup.patchValue({ title: titleValue });
        }

        if (!this.processConfig.description) {
            let descriptionIndex = (this.processConfig.questions.findIndex((question) => question.typeConfig.type === QuestionTypeEnum.Description) + 1);
            let descriptionValue = formGroup.get('field' + descriptionIndex) ? formGroup.get('field' + descriptionIndex).value : '';
            formGroup.patchValue({ description: descriptionValue });
        }

        if (!this.processConfig.deadline) {
            let deadlineIndex = (this.processConfig.questions.findIndex((question) => question.typeConfig.type === QuestionTypeEnum.Deadline) + 1);
            let deadlineValue = formGroup.get('field' + deadlineIndex) ? formGroup.get('field' + deadlineIndex).value : '';
            formGroup.patchValue({ deadline: deadlineValue });
        }

        if (!this.processConfig.initialDeadline) {
            let initialDeadlineIndex = (this.processConfig.questions.findIndex((question) => question.typeConfig.type === QuestionTypeEnum.InitialDeadline) + 1);
            let initialDeadlineValue = formGroup.get('field' + initialDeadlineIndex) ? formGroup.get('field' + initialDeadlineIndex).value : '';
            formGroup.patchValue({ initialDeadline: initialDeadlineValue });
        }

        if (!this.processConfig.weight) {
            let weightIndex = (this.processConfig.questions.findIndex((question) => question.typeConfig.type === QuestionTypeEnum.Weight) + 1);
            let weightValue = formGroup.get('field' + weightIndex) ? formGroup.get('field' + weightIndex).value : 0;
            formGroup.patchValue({ weight: weightValue });
        }

        let isValidDeadlines = isNil(formGroup.value.initialDeadline)
            || this._validateDeadline(formGroup.value.deadline, formGroup.value.initialDeadline);

        if (!isValidDeadlines) {
            this.notify(
                this._translationService.getTranslation(
                    'generic.invalidDeadlines'
                ), 'danger'
            );
            return;
        }

        if (this.consent) {
            let goal = this.addConsentGoal(formGroup);
            this.dismiss(false, goal);
            return;
        }

        this._goalId ?
            this.updateGoal(formGroup) :
            this.addNewGoal(formGroup);
    }

    public checkWeight(value) {
        if (value === '' || value > 100) {
            this.formGroup.controls.weight.setValue(0);
        }
    }

    public async addNewGoal(formGroup: UntypedFormGroup): Promise<void> {
        if (formGroup.valid) {
            const goal: Goal = formGroup.getRawValue();
            goal.deadline = this._clearHours(goal.deadline);

            const loading = await this._loadingController.create();
            await loading.present();

            goal.processType = this.processType;

            this._goalsService.addNewGoal(goal).subscribe(() => {
                loading.dismiss();
                this.notify('Meta adicionada com sucesso', 'success');
                this.dismiss(true);

            }, (error) => {
                loading.dismiss();
                this.notify(this.getErrorNotification(error), 'danger');
            });
        } else {
            this.notify(
                this._translationService.getTranslation(
                    'generic.missingFields'
                ), 'danger'
            );
        }
    }

    public updateGoal(formGroup: UntypedFormGroup): void {
        if (formGroup.valid) {
            const goal = this._setGoalId(
                formGroup.getRawValue() as Goal
            );
            goal.deadline = this._clearHours(goal.deadline);
            goal.deadline = this._clearHours(goal.initialDeadline);

            this._navParams.get('isGoalReview') ?
                this._updateGoalOnReview(goal) :
                this._updateGoalOnInput(goal);

        } else {
            this.notify(
                this._translationService.getTranslation(
                    'generic.missingFields'
                ), 'danger'
            );
        }
    }

    private async _updateGoalOnInput(goal: Goal): Promise<void> {
        const loading = await this._loadingController.create();
        await loading.present();
        this._goalsService.updateGoal(goal).subscribe(() => {
            loading.dismiss();
            this.notify('Meta atualizada com sucesso', 'success');
            this.dismiss(true);

        }, (error) => {
            loading.dismiss();
            this.notify(this.getErrorNotification(error), 'danger');
        });
    }

    private async _updateGoalOnReview(goal: Goal): Promise<void> {
        const loading = await this._loadingController.create();
        await loading.present();

        this._goalsService.updateGoalsForReview(goal).subscribe(() => {
            loading.dismiss();
            this.notify('Meta atualizada com sucesso', 'success');
            this.dismiss(true);

        }, (error) => {
            loading.dismiss();
            this.notify(this.getErrorNotification(error), 'danger');
        });
    }


    public addConsentGoal(formGroup: UntypedFormGroup): Goal {
        if (formGroup.valid) {
            const goal: Goal = formGroup.getRawValue();
            goal.deadline = this._clearHours(goal.deadline);
            return goal;
        } else {
            this.notify(
                this._translationService.getTranslation(
                    'generic.missingFields'
                ), 'danger'
            );
        }
    }

    private _validateDeadline(deadline: string | Date, initialDeadline: string | Date): boolean {
        const deadlineDate = new Date(deadline);
        const initialDeadlineDate = initialDeadline ? new Date(initialDeadline) : null;
        return !(!isNil(initialDeadline) && deadlineDate <= initialDeadlineDate);
    }

    private _createGoalFormGroup(questions: Array<QuestionConfig>, editingGoal: Goal = null): UntypedFormGroup {
        // Add defensive check for processConfig
        if (!this.processConfig || !this.processConfig.questions) {
            questions = questions || [];
            // Create a basic form group when processConfig is not available
            return new UntypedFormGroup({
                title: new UntypedFormControl(editingGoal ? editingGoal.title : ''),
                description: new UntypedFormControl(editingGoal ? editingGoal.description : ''),
                deadline: new UntypedFormControl(editingGoal ? editingGoal.deadline : ''),
                weight: new UntypedFormControl(editingGoal ? editingGoal.weight : 0)
            });
        }

        let hasWeightConfig = this.processConfig.questions.some((question) => question.typeConfig.type === QuestionTypeEnum.Weight);
        let hasTitleConfig = this.processConfig.questions.some((question) => question.typeConfig.type === QuestionTypeEnum.Title);
        let hasDescriptionConfig = this.processConfig.questions.some((question) => question.typeConfig.type === QuestionTypeEnum.Description);
        let hasDeadlineConfig = this.processConfig.questions.some((question) => question.typeConfig.type === QuestionTypeEnum.Deadline);
        let hasInitialDeadlineConfig = this.processConfig.questions.some((question) => question.typeConfig.type === QuestionTypeEnum.InitialDeadline);

        const formGroup = new UntypedFormGroup({
            'cycleParticipantId': new UntypedFormControl(this._participantId),
            'weight': new UntypedFormControl(
                    editingGoal ? editingGoal.weight:'',
                    hasWeightConfig ? [Validators.required, Validators.min(0), Validators.max(100)]:null
            ),
            'title': new UntypedFormControl(
                    editingGoal ? editingGoal.title:'',
                    hasTitleConfig ? [Validators.required]:null
            ),
            'description': new UntypedFormControl(
                    editingGoal ? editingGoal.description:'',
                    hasDescriptionConfig ? [Validators.required]:null
            ),
            'deadline': new UntypedFormControl(
                    editingGoal ? editingGoal.deadline:'',
                    hasDeadlineConfig ? [Validators.required]:null
            ),
            'initialDeadline': new UntypedFormControl(
                    editingGoal ? editingGoal.initialDeadline:'',
                    hasInitialDeadlineConfig ? [Validators.required]:null
            ),
            'id': new UntypedFormControl(
                    editingGoal ? editingGoal.id:null
            ),
        });

        questions.forEach((question, index) => {
            const controlName = 'field' + (index + 1);
            formGroup.addControl(
                    controlName,
                    new UntypedFormControl(
                            editingGoal ? editingGoal[controlName]:'',
                            question.required ? question.typeConfig.type === this.QuestionType.Weight ? [Validators.required, Validators.min(1), Validators.max(100)]:[Validators.required]:[]
                    )
            );
        });

        return formGroup;
    }

    public getTypeValues(typeConfig: QuestionTypeConfig, index: number) {
        let isConsentTypeConfigValid = typeConfig && !isNil(typeConfig.isConsentPossibleValues);
        if ((this.consent && isConsentTypeConfigValid && typeConfig.isConsentPossibleValues[index])
            || !this.consent && !isConsentTypeConfigValid
            || !isConsentTypeConfigValid
            || isConsentTypeConfigValid && !typeConfig.isConsentPossibleValues[index]) {
            let globalizedValues = typeConfig.possibleValuesGlobalized;
            return (globalizedValues[this.language][index]);
        }
    }

    public isOptionVisible(typeConfig: QuestionTypeConfig, index: number): boolean {
        let isConsentTypeConfigValid = typeConfig && !isNil(typeConfig.isConsentPossibleValues);
        if ((this.consent && isConsentTypeConfigValid && typeConfig.isConsentPossibleValues[index])
            || !this.consent && !isConsentTypeConfigValid
            || !isConsentTypeConfigValid
            || isConsentTypeConfigValid && !typeConfig.isConsentPossibleValues[index]) {
            return true;
        } else {
            return false;
        }
    }

    private _clearHours(deadline: string | Date, isDeadline = true): Date {
        deadline = new Date(deadline);
        deadline.setHours(isDeadline ? 23 : 0, isDeadline ? 59 : 0, isDeadline ? 59 : 0, isDeadline ? 59 : 0);
        return deadline;
    }

    private _setGoalId(goal: Goal): Goal {
        goal.id = this._goalId;
        return goal;
    }

}
